"use client"

import { Container } from "components/layout/Container"
import React from "react"
import Image from "next/image"

import screenshotAlgorithm from "./components/images/algorithm.webp"
import screenshotLanguages from "./components/images/languages.webp"
import screenshotRearrange from "./components/images/rearrange.webp"
import screenshotStyling from "./components/images/styling.webp"
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react"
import { clsx } from "clsx"

const primaryFeatures = [
    {
        title: "Rearrange words",
        description: "You can rearrange the words in the word cloud to make it make the result more expressive.",
        image: screenshotRearrange,
    },
    {
        title: "Styling",
        description:
            "Select from many fonts (including Google Fonts), colors and text styling options to make your word cloud look great.",
        image: screenshotStyling,
    },
    {
        title: "Stop words",
        description:
            "Stop words like 'the' and 'be' are removed from the word cloud. Automatically loading stop words for 50 different languages.",
        image: screenshotLanguages,
    },
    {
        title: "Multiple algorithms",
        description:
            "Several different algorithms are available to generate the word cloud. You can choose the one that best suits your needs.",
        image: screenshotAlgorithm,
    },
]

function PrimaryFeatures() {
    return (
        <section
            id="features"
            aria-label="Features for creating informative word clouds"
            className="relative overflow-hidden py-12"
        >
            <Container className="relative">
                <div className="max-w-2xl md:mx-auto md:text-center xl:max-w-none">
                    <h2 className="font-display text-3xl tracking-tight sm:text-4xl md:text-5xl">
                        Power to create informative word clouds.
                    </h2>
                    <p className="mt-6 text-lg tracking-tight text-gray-800">
                        We get you started using cutting-edge algorithms and then hand the controls over to you to
                        create the most effective word clouds.
                    </p>
                </div>
                <TabGroup
                    as="div"
                    className="mt-16 grid grid-cols-1 items-center gap-y-2 pt-10 sm:gap-y-6 md:mt-20 lg:grid-cols-12 lg:pt-0"
                >
                    {({ selectedIndex }) => (
                        <>
                            <div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5">
                                <TabList className="relative z-10 flex gap-x-4 px-4 whitespace-nowrap sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                                    {primaryFeatures.map((feature, featureIndex) => (
                                        <div
                                            key={feature.title}
                                            className={clsx(
                                                "group relative rounded-full px-4 py-1 lg:rounded-l-xl lg:rounded-r-none lg:p-6",
                                                selectedIndex === featureIndex
                                                    ? "bg-primary-50/50 lg:ring-primary-600/10 lg:ring-1 lg:ring-inset"
                                                    : "hover:bg-white/10 lg:hover:bg-white/5"
                                            )}
                                        >
                                            <h3>
                                                <Tab
                                                    className={clsx(
                                                        "font-display text-lg outline-none",
                                                        selectedIndex === featureIndex
                                                            ? "text-primary-800 font-semibold"
                                                            : "text-gray-800 hover:text-black hover:underline"
                                                    )}
                                                >
                                                    <span className="absolute inset-0 rounded-full lg:rounded-l-xl lg:rounded-r-none" />
                                                    {feature.title}
                                                </Tab>
                                            </h3>
                                            <p
                                                className={clsx(
                                                    "mt-2 hidden text-sm lg:block",
                                                    selectedIndex === featureIndex
                                                        ? "text-gray-900"
                                                        : "text-gray-700 group-hover:text-black"
                                                )}
                                            >
                                                {feature.description}
                                            </p>
                                        </div>
                                    ))}
                                </TabList>
                            </div>
                            <TabPanels className="lg:col-span-7">
                                {primaryFeatures.map((feature) => (
                                    <TabPanel key={feature.title} unmount={false}>
                                        <div className="relative sm:px-6 lg:hidden">
                                            <div className="absolute -inset-x-4 top-[-6.5rem] bottom-[-4.25rem] bg-white/10 ring-1 ring-white/10 ring-inset sm:inset-x-0 sm:rounded-t-xl" />
                                            <p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
                                                {feature.description}
                                            </p>
                                        </div>
                                        <div className="mt-10 w-[45rem] overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-purple-900/20 sm:w-auto lg:mt-0 lg:w-[67.8125rem]">
                                            <Image
                                                className="w-full"
                                                src={feature.image}
                                                alt=""
                                                sizes="(min-width: 1024px) 67.8125rem, (min-width: 640px) 100vw, 45rem"
                                                placeholder="blur"
                                            />
                                        </div>
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </>
                    )}
                </TabGroup>
            </Container>
        </section>
    )
}

export default function Page() {
    return (
        <Container>
            <PrimaryFeatures />
        </Container>
    )
}
